import React from 'react';

export interface NewSellingGrossData {
  newVehicleSales: number;
  newVehicleCOS: number;
  newVehicleGross: number;
  fiGross: number;
  totalGross: number;
  variableExpenses: Array<{ accountCode: string; description: string; amount: number }>;
  totalVariableExpenses: number;
  semiFixedExpenses: Array<{ accountCode: string; description: string; amount: number }>;
  totalSemiFixedExpenses: number;
  totalExpenses: number;
  sellingGross: number;
}

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function formatCurrency(value: number): string {
  if (value < 0) {
    return `(${fmt.format(Math.abs(value))})`;
  }
  return fmt.format(value);
}

interface Props {
  data: NewSellingGrossData;
}

export const NewSellingGrossReport: React.FC<Props> = ({ data }) => {
  const {
    newVehicleSales,
    newVehicleCOS,
    newVehicleGross,
    fiGross,
    totalGross,
    variableExpenses,
    totalVariableExpenses,
    semiFixedExpenses,
    totalSemiFixedExpenses,
    sellingGross,
  } = data;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-300">
            <th className="text-left py-2 px-3 font-semibold text-gray-700 w-3/4">Description</th>
            <th className="text-right py-2 px-3 font-semibold text-gray-700 w-1/4">Amount</th>
          </tr>
        </thead>
        <tbody>
          {/* Vehicle Gross */}
          <Row label="New Vehicle Sales (Revenue)" value={newVehicleSales} />
          <Row label="New Vehicle Cost of Sales" value={newVehicleCOS} negative />
          <BoldRow label="NEW VEHICLE GROSS" value={newVehicleGross} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-1" /></tr>

          {/* F&I Gross */}
          <BoldRow label="F&I GROSS (NEW)" value={fiGross} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-1" /></tr>

          {/* Total Gross - highlighted */}
          <tr className="bg-blue-50 border-t-2 border-b-2 border-blue-200">
            <td className="py-3 px-3 font-bold text-blue-900 text-base">TOTAL GROSS</td>
            <td className={`py-3 px-3 text-right font-bold text-base font-mono ${totalGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
              {formatCurrency(totalGross)}
            </td>
          </tr>

          {/* Separator */}
          <tr><td colSpan={2} className="py-2" /></tr>

          {/* Variable Expenses */}
          <tr>
            <td colSpan={2} className="py-1 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Variable Expenses
            </td>
          </tr>
          {variableExpenses.length > 0 ? (
            variableExpenses.map((exp) => (
              <Row
                key={exp.accountCode}
                label={`${exp.accountCode} - ${exp.description}`}
                value={exp.amount}
                indent
              />
            ))
          ) : (
            <tr>
              <td colSpan={2} className="py-1 px-3 pl-6 text-gray-400 italic">
                No variable expense data for this period
              </td>
            </tr>
          )}
          <BoldRow label="TOTAL VARIABLE EXPENSES" value={totalVariableExpenses} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-2" /></tr>

          {/* Semi-Fixed Expenses */}
          <tr>
            <td colSpan={2} className="py-1 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Semi-Fixed Expenses
            </td>
          </tr>
          {semiFixedExpenses.length > 0 ? (
            semiFixedExpenses.map((exp) => (
              <Row
                key={exp.accountCode}
                label={`${exp.accountCode} - ${exp.description}`}
                value={exp.amount}
                indent
              />
            ))
          ) : (
            <tr>
              <td colSpan={2} className="py-1 px-3 pl-6 text-gray-400 italic">
                No semi-fixed expense data for this period
              </td>
            </tr>
          )}
          <BoldRow label="TOTAL SEMI-FIXED EXPENSES" value={totalSemiFixedExpenses} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-2" /></tr>

          {/* Selling Gross - highlighted */}
          <tr className="bg-blue-50 border-t-2 border-b-2 border-blue-200">
            <td className="py-3 px-3 font-bold text-blue-900 text-base">NEW VEHICLE DEPT SELLING GROSS</td>
            <td className={`py-3 px-3 text-right font-bold text-base font-mono ${sellingGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
              {formatCurrency(sellingGross)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

function Row({ label, value, indent, negative }: { label: string; value: number; indent?: boolean; negative?: boolean }) {
  const isNeg = value < 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`py-1.5 px-3 text-gray-700 ${indent ? 'pl-6' : ''}`}>{label}</td>
      <td className={`py-1.5 px-3 text-right font-mono ${(isNeg || negative) ? 'text-red-600' : 'text-gray-900'}`}>
        {formatCurrency(negative ? -Math.abs(value) : value)}
      </td>
    </tr>
  );
}

function BoldRow({ label, value }: { label: string; value: number }) {
  const isNeg = value < 0;
  return (
    <tr className="border-b-2 border-gray-200 bg-gray-50">
      <td className="py-2 px-3 font-semibold text-gray-900">{label}</td>
      <td className={`py-2 px-3 text-right font-semibold font-mono ${isNeg ? 'text-red-600' : 'text-gray-900'}`}>
        {formatCurrency(value)}
      </td>
    </tr>
  );
}
